/**
 * 양식 편집 「자동 정보」 — 문서 종류마다 실제로 채워지는 정보만 묶음별로 보여 주고,
 * 채워지지 않을 {{…}} 를 찾아 저장 전에 알린다.
 *
 * 채워지는 정보는 서버 렌더러가 넣는 값과 같아야 한다(어긋나면 발급 · 결재 문서에서 오류 없이 빈칸):
 * - 모든 문서: buildBaseVariables(document-form.service.ts) — 직원 · 기관(주소 제외) · 승인자 · 서명
 * - 증명서: + renderCertificateIssuance 의 기관 주소 · 발급일 · 증명서 번호 + 신청 항목(requestDetailVariables)
 * - 휴가 · 시간외근무 · 외근 신청서: + 기안일 · 신청일 + 그 종류의 값(renderLeaveRequestForm …)
 * - 그 밖의 결재 서식(사직서 · 휴직 · 인사발령 · 구매 · 지출 · 출장): 본문이 결재 문서로 그려지지 않는다 — 공통 정보만 보여 준다.
 */
import { FORM_TEMPLATE_CODES, isCertificateTemplateCode } from "@hereby/core/constants";

export interface DocVariable {
  key: string;
  label: string;
  example: string;
}

export interface DocVariableGroup {
  id: string;
  label: string;
  variables: DocVariable[];
}

const EMPLOYEE = [
  "name",
  "birthDate",
  "address",
  "employeeNumber",
  "position",
  "departmentName",
  "hireDate",
  "applicantSignature",
];
const ORG_BASE = ["orgName", "orgLegalName", "orgPhone", "orgTaxId", "representativeName"];
const CERT_ISSUE = [
  "purpose",
  "submitTo",
  "quantity",
  "requestNote",
  "issueDate",
  "certificateNumber",
  "approverName",
];
const APPROVAL = ["draftDate", "applyDate", "approverName"];
const LEAVE = ["leaveType", "leavePeriod", "leaveDays", "leaveReason"];
const OVERTIME = [
  "overtimeDate",
  "overtimeTime",
  "overtimeHours",
  "overtimeType",
  "overtimeReason",
];
const OFFSITE = ["offsiteDate", "offsiteTime", "offsiteLocation", "offsiteType", "offsiteReason"];

function pick(catalog: readonly DocVariable[], keys: readonly string[]): DocVariable[] {
  return keys
    .map((k) => catalog.find((v) => v.key === k))
    .filter((v): v is DocVariable => Boolean(v));
}

/** 이 문서(코드)에서 채워지는 정보 — 묶음별 */
export function variableGroupsFor(
  code: string,
  catalog: readonly DocVariable[],
): DocVariableGroup[] {
  const groups: DocVariableGroup[] = [
    { id: "employee", label: "직원", variables: pick(catalog, EMPLOYEE) },
  ];
  if (isCertificateTemplateCode(code)) {
    groups.push({
      id: "org",
      label: "기관",
      variables: pick(catalog, [...ORG_BASE.slice(0, 2), "orgAddress", ...ORG_BASE.slice(2)]),
    });
    groups.push({ id: "issue", label: "신청 · 발급", variables: pick(catalog, CERT_ISSUE) });
    return groups;
  }
  groups.push({ id: "org", label: "기관", variables: pick(catalog, ORG_BASE) });
  groups.push({ id: "approval", label: "결재", variables: pick(catalog, APPROVAL) });
  if (code === FORM_TEMPLATE_CODES.LEAVE_REQUEST) {
    groups.push({ id: "leave", label: "휴가", variables: pick(catalog, LEAVE) });
  } else if (code === FORM_TEMPLATE_CODES.OVERTIME_REQUEST) {
    groups.push({ id: "overtime", label: "시간외근무", variables: pick(catalog, OVERTIME) });
  } else if (code === FORM_TEMPLATE_CODES.OFFSITE_WORK) {
    groups.push({ id: "offsite", label: "외근", variables: pick(catalog, OFFSITE) });
  }
  return groups.filter((g) => g.variables.length > 0);
}

/** 본문의 {{…}} 이름들(나온 순서, 중복 없이). 앞뒤 공백은 서버와 같게 그대로 둔다 */
export function placeholderNames(body: string): string[] {
  const out: string[] = [];
  for (const m of body.matchAll(/\{\{([^}]+)\}\}/g)) {
    if (!out.includes(m[1])) {
      out.push(m[1]);
    }
  }
  return out;
}

/** 이 문서에서 채워지지 않을 {{…}} — 다른 문서용 정보 · 오타 · 없는 이름. 이름(label)이나 key 둘 다 받는다(서버와 같음) */
export function unfilledPlaceholders(body: string, groups: readonly DocVariableGroup[]): string[] {
  const ok = new Set<string>();
  for (const g of groups) {
    for (const v of g.variables) {
      ok.add(v.label);
      ok.add(v.key);
    }
  }
  return placeholderNames(body).filter((n) => !ok.has(n));
}
